import axios from 'axios'
import API from '@/services/api'
import logger from '@/logger/index'
import store from '@/store'
import { logsService } from '@/services'
import recaptcha from '@/services/recaptcha'
import sessionToken from '@/services/sessionToken'
import { validateFtsImageUrl } from '@/services/ftsImageUrl'
import {
  CHECK_PRODUCT_STOCK,
  CHECK_PRODUCT_STOCK_ARRAY,
  CHECK_PRODUCT_PRICE,
  SEARCH_PRODUCT,
  GET_PRODUCT,
  GET_BATCH_PRODUCTS,
  GET_PARTS,
  GET_PARTS_PRODUCT,
  GET_PRODUCT_RELATED,
  GET_PRICE_ADD,
  UPDATE_PRICE_ADD
} from '@/services/enpoints'

const IMAGE_TIMEOUT_MS = 45000

let imageTokenRefresh = null

async function refreshFtsImageToken (staleToken) {
  const currentToken = sessionToken.getToken()
  if (currentToken && currentToken !== staleToken) return currentToken

  if (!imageTokenRefresh) {
    sessionToken.clearToken()
    imageTokenRefresh = sessionToken.ensureToken()
      .finally(() => { imageTokenRefresh = null })
  }

  return imageTokenRefresh
}

async function requestFtsImage (imageUrl, allowRefresh = true) {
  const token = await sessionToken.ensureToken()
  if (!token) return null

  try {
    return await axios({
      method: 'GET',
      url: imageUrl,
      responseType: 'blob',
      timeout: IMAGE_TIMEOUT_MS,
      headers: { Authorization: token },
    })
  } catch (error) {
    if (allowRefresh && error.response && error.response.status === 401) {
      const freshToken = await refreshFtsImageToken(token)
      if (freshToken) return requestFtsImage(imageUrl, false)
    }
    throw error
  }
}

const checkStock = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: CHECK_PRODUCT_STOCK,
    data,
    requiresSessionToken: true,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const checkPrice = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: CHECK_PRODUCT_PRICE,
    data,
    requiresSessionToken: true,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const checkStockArray = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: CHECK_PRODUCT_STOCK_ARRAY,
    data,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

/**
 * Lê X-Session-Token da resposta. Em CORS, o backend precisa enviar
 * Access-Control-Expose-Headers: X-Session-Token para o header ficar acessível.
 */
function getSessionTokenFromResponse (response) {
  if (!response || !response.headers) return null
  const h = response.headers
  return h['x-session-token'] || h['X-Session-Token'] || (typeof h.get === 'function' ? h.get('X-Session-Token') : null) || null
}

/**
 * Garante que o token tenha o prefixo "Bearer " antes de persistir/enviar.
 */
function normalizeSessionToken (token) {
  if (!token || typeof token !== 'string') return token
  const trimmed = token.trim()
  if (!trimmed) return token
  return /^bearer\s+/i.test(trimmed) ? trimmed : `Bearer ${trimmed}`
}

/**
 * Faz a requisição de busca e persiste X-Session-Token da resposta em cookie.
 */
function doSearchRequest (data, headers = {}) {
  return API.call({
    method: 'POST',
    url: SEARCH_PRODUCT,
    data,
    headers: Object.keys(headers).length ? headers : undefined,
  }).then(response => {
    const rawToken = getSessionTokenFromResponse(response)
    if (rawToken) {
      const tokenToPersist = normalizeSessionToken(rawToken)
      sessionToken.setToken(tokenToPersist)
    }
    return response
  })
}

/**
 * Busca produtos. Usa session token (cookie) se existir; senão gera reCAPTCHA.
 * Em caso de 403 (token de sessão inválido), limpa o cookie e tenta novamente com reCAPTCHA.
 */
const search = async (data) => {
  data.token = store.state.auth.token
  const tokenFromCookie = sessionToken.getToken()
  let headers = {}

  if (tokenFromCookie) {
    headers.Authorization = tokenFromCookie
  } else {
    data.recaptcha = await recaptcha.getRecaptchaToken()
  }

  try {
    return await doSearchRequest(data, headers)
  } catch (error) {
    if (error.response && error.response.status === 403) {
      sessionToken.clearToken()
      delete data.recaptcha
      data.recaptcha = await recaptcha.getRecaptchaToken()
      return doSearchRequest(data, {})
    }
    logsService.logs({ error })
    throw error
  }
}

const get = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: GET_PRODUCT,
    data,
    requiresSessionToken: true,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const getBatchProducts = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: GET_BATCH_PRODUCTS,
    data,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const getParts = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: GET_PARTS,
    data,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const getPartsProducts = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: GET_PARTS_PRODUCT,
    data,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const getRelated = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: GET_PRODUCT_RELATED,
    data,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const getPriceAdd = () => {
  const authToken = store.state.auth.token
  return API.call({
    method: 'POST',
    url: GET_PRICE_ADD,
    data: { 'token': authToken },
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const updatePriceAdd = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: UPDATE_PRICE_ADD,
    data,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const getProductImage = (productIdOrUrl) => {
  const token = store.state.auth.token
  const cnpj = store.state.auth.cnpj

  if (!token || !cnpj) {
    return Promise.resolve(null)
  }

  let imageUrl = null

  // Se for uma string (URL), usa diretamente
  if (typeof productIdOrUrl === 'string' && productIdOrUrl.startsWith('http')) {
    imageUrl = productIdOrUrl
  } else if (typeof productIdOrUrl === 'number') {
    // Se for um número (ID), busca no selectedProduct (compatibilidade)
    const selectedProduct = store.state.products.selectedProduct
    if (selectedProduct && selectedProduct.id === productIdOrUrl && selectedProduct.image) {
      imageUrl = selectedProduct.image
    }
  }

  // Se não encontrou URL, retorna null
  if (!imageUrl) {
    return Promise.resolve(null)
  }

  // A imagem é externa à API principal. Envia somente o Bearer específico
  // do FTS e não passa pelos interceptors globais de autenticação/logout.
  const validation = validateFtsImageUrl(imageUrl)
  if (!validation.trusted) {
    logger.warn({
      reason: validation.reason,
      origin: validation.origin,
      pathname: validation.pathname,
    }, 'URL de imagem FTS rejeitada')
    return Promise.resolve(null)
  }

  return requestFtsImage(imageUrl).then(response => response && response.data).catch(error => {
    logger.debug('Erro na requisição da imagem:', error)
    logsService.logs({ error: error })
    throw error
  })
}

export const productService = {
  checkStock,
  checkPrice,
  checkStockArray,
  search,
  get,
  getBatchProducts,
  getProductImage,
  getParts,
  getPartsProducts,
  getRelated,
  getPriceAdd,
  updatePriceAdd,
}
